import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import VideoPlayer from '../components/VideoPlayer';

interface SessionInfo {
  authenticated: boolean;
  streamId?: string;
  streamTitle?: string;
  expiresAt?: string;
  error?: string;
}

interface StreamInfo {
  id: string;
  title: string;
  description: string | null;
  isLive: boolean;
  streamKey?: string;
}

export default function Watch() {
  const { streamId } = useParams<{ streamId: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [stream, setStream] = useState<StreamInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const heartbeatInterval = useRef<number | null>(null);

  useEffect(() => {
    checkSession();
    return () => {
      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
      }
    };
  }, [streamId]);

  async function checkSession() {
    try {
      const res = await fetch('/api/access/session', {
        credentials: 'include'
      });
      const data: SessionInfo = await res.json();

      if (!data.authenticated) {
        navigate('/redeem', { state: { returnTo: `/watch/${streamId}` } });
        return;
      }

      if (data.streamId !== streamId) {
        setError('Your access code is not valid for this stream.');
        setLoading(false);
        return;
      }

      setSession(data);

      // Fetch stream details
      const streamRes = await fetch(`/api/streams/${streamId}`);
      if (streamRes.ok) {
        const streamData = await streamRes.json();
        setStream(streamData);
      }

      // Start heartbeat to track viewer
      startHeartbeat();
    } catch (err) {
      setError('Failed to verify access. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function startHeartbeat() {
    // Send heartbeat every 30 seconds
    heartbeatInterval.current = window.setInterval(async () => {
      try {
        const res = await fetch('/api/access/heartbeat', {
          method: 'POST',
          credentials: 'include'
        });

        if (!res.ok) {
          // Session expired
          clearInterval(heartbeatInterval.current!);
          setError('Your access has expired.');
        }
      } catch (err) {
        console.error('Heartbeat failed:', err);
      }
    }, 30000);
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="center-page">
        <div className="center-card">
          <h1>Access Error</h1>
          <p>{error}</p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '20px' }}>
            <Link to="/redeem" className="btn btn-primary">
              Enter Code
            </Link>
            <Link to="/" className="btn btn-secondary">
              Browse Streams
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container">
        {/* Video Player */}
        <div className="video-container">
          {stream?.isLive ? (
            <VideoPlayer streamId={streamId!} />
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#000',
              color: '#fff'
            }}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="1.5">
                <polygon points="23 7 16 12 23 17 23 7"></polygon>
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
              </svg>
              <p style={{ marginTop: '20px', color: '#999' }}>
                Stream is currently offline
              </p>
              <p style={{ fontSize: '0.9rem', color: '#666' }}>
                The stream will appear here when it goes live
              </p>
            </div>
          )}
        </div>

        {/* Stream Info */}
        <div style={{ maxWidth: '1000px', margin: '30px auto 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <h1 style={{ fontSize: '1.5rem' }}>{session?.streamTitle || stream?.title}</h1>
            {stream?.isLive && (
              <span className="badge badge-live">
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'white', display: 'inline-block' }}></span>
                LIVE
              </span>
            )}
          </div>

          {stream?.description && (
            <p style={{ color: 'var(--text-muted)', lineHeight: '1.7' }}>
              {stream.description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
